import { useNavigate } from "react-router-dom";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface ProductNavigationProps {
  activeCategory: "consumer" | "power";
  currentProductId?: string;
  productsData: any;
  allProducts: any;
}

export default function ProductNavigation({
  activeCategory,
  currentProductId,
  productsData,
  allProducts,
}: ProductNavigationProps) {
  const navigate = useNavigate();
  const { ref, isVisible } = useScrollAnimation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(true);

  const otherProductsInCategory = Object.keys(productsData[activeCategory]);

  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } =
          scrollContainerRef.current;
        setShowLeftScroll(scrollLeft > 0);
        setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    checkScroll();
    const container = scrollContainerRef.current;
    container?.addEventListener("scroll", checkScroll);

    window.addEventListener("resize", checkScroll);

    return () => {
      container?.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [otherProductsInCategory.length]);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      const newScrollLeft =
        scrollContainerRef.current.scrollLeft +
        (direction === "left" ? -scrollAmount : scrollAmount);

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });
    }
  };

  if (otherProductsInCategory.length === 0) return null;

  return (
    <section
      ref={ref}
      className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-sm"
    >
      <div
        className={`max-w-7xl mx-auto px-4 sm:px-6 transition-all duration-700 relative ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        {showLeftScroll && (
          <button
            onClick={() => scroll("left")}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-full p-2 shadow-lg hover:bg-white transition-all md:hidden"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700" />
          </button>
        )}

        {showRightScroll && (
          <button
            onClick={() => scroll("right")}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-full p-2 shadow-lg hover:bg-white transition-all md:hidden"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-5 h-5 text-gray-700" />
          </button>
        )}

        <div className="relative py-4">
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none md:hidden" />
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none md:hidden" />

          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto py-1 gap-3 sm:gap-4 scrollbar-hide sm:justify-center scroll-container"
          >
            {otherProductsInCategory.map((navProductId, index) => {
              const navProduct =
                allProducts[navProductId as keyof typeof allProducts];
              const isActive = navProductId === currentProductId;
              return (
                <button
                  key={navProductId}
                  onClick={() => navigate(`/products/${navProductId}`)}
                  className={`flex-shrink-0 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl transition-all duration-500 transform hover:scale-105 min-w-max ${
                    isActive
                      ? "border-2 border-sky-500 text-sky-500 shadow-lg shadow-sky-500/25 font-semibold bg-sky-50"
                      : "border border-gray-300 sm:border-2 sm:border-gray-300 text-gray-800 shadow-sm hover:shadow-md hover:bg-gray-50"
                  } ${index === 0 ? "ml-2 sm:ml-0" : ""} ${
                    index === otherProductsInCategory.length - 1
                      ? "mr-2 sm:mr-0"
                      : ""
                  }`}
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: isVisible
                      ? `slideInRight 0.6s ease-out ${index * 100}ms both`
                      : "none",
                  }}
                >
                  <span className="font-medium whitespace-nowrap text-sm sm:text-base">
                    {navProduct.title}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .scroll-container {
            scroll-padding-left: 16px;
            scroll-padding-right: 16px;
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
          }
          
          /* Hide scrollbar for Chrome, Safari and Opera */
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
            height: 0;
          }
          
          /* Hide scrollbar for IE, Edge and Firefox */
          .scrollbar-hide {
            -ms-overflow-style: none;  /* IE and Edge */
            scrollbar-width: none;  /* Firefox */
          }
          
          /* Add subtle bounce animation on load for mobile */
          @keyframes mobileSlideIn {
            0% {
              opacity: 0;
              transform: translateX(-10px);
            }
            100% {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          .scroll-container {
            animation: mobileSlideIn 0.5s ease-out;
          }
        }

        /* Touch device optimizations */
        @media (hover: none) and (pointer: coarse) {
          button {
            padding-left: 20px;
            padding-right: 20px;
            min-height: 44px; /* Minimum touch target size */
          }
        }
      `}</style>
    </section>
  );
}
